import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Shield, ArrowLeft, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MFA OTP Verification Page
 * 6-digit OTP input with auto-submit and resend functionality
 */
const MFAVerification = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [challengeId, setChallengeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [mobileLastDigits, setMobileLastDigits] = useState('****');
  
  const inputRefs = useRef([]);

  // Get challenge ID from session storage
  useEffect(() => {
    const storedChallengeId = sessionStorage.getItem('mfa_challenge');
    const storedMobile = sessionStorage.getItem('mfa_mobile');
    
    if (!storedChallengeId) {
      // No challenge, redirect to login
      navigate('/login');
      return;
    }
    
    setChallengeId(storedChallengeId);
    if (storedMobile) {
      setMobileLastDigits(storedMobile);
    }
  }, [navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only last character
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all filled
    if (newOtp.every(d => d) && newOtp.join('').length === 6) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
      
      // Auto-submit
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (otpValue = otp.join('')) => {
    if (otpValue.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/mfa/verify`, {
        challenge_id: challengeId,
        otp: otpValue
      });

      if (response.data.ok && response.data.token) {
        // Store token and user data
        const token = response.data.token;
        const userData = response.data.user || {};
        
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Clear MFA session data
        sessionStorage.removeItem('mfa_challenge');
        sessionStorage.removeItem('mfa_mobile');
        
        // Login and redirect
        login(userData, token);
        navigate('/');
      } else {
        setError('Verification failed. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Invalid OTP. Please try again.';
      setError(errorMsg);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    
    setError('');
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/mfa/resend?challenge_id=${challengeId}`);
      setResendCooldown(60);
      setError(''); // Clear any previous errors
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to resend OTP';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    sessionStorage.removeItem('mfa_challenge');
    sessionStorage.removeItem('mfa_mobile');
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>MFA Verification | URGAA</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#F7F7FA] to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to login</span>
          </button>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-[#570683] bg-opacity-10 rounded-full flex items-center justify-center">
                <Shield className="text-[#570683]" size={32} />
              </div>
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Two-Factor Authentication
              </h1>
              <p className="text-sm text-gray-600">
                Enter the 6-digit code sent to your mobile ending in {mobileLastDigits}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 text-center">
                {error}
              </div>
            )}

            {/* OTP Input */}
            <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-[#0DA34E] focus:ring-2 focus:ring-[#0DA34E] focus:ring-opacity-20 outline-none transition-all"
                  autoFocus={index === 0}
                  disabled={loading}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              onClick={() => handleVerify()}
              disabled={loading || otp.some(d => !d)}
              className="w-full py-3 bg-gradient-to-r from-[#570683] to-[#417E46] text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 mb-4"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Verifying...</span>
                </>
              ) : (
                'Verify Code'
              )}
            </button>

            {/* Resend */}
            <div className="text-center">
              <button
                onClick={handleResend}
                disabled={resendCooldown > 0 || loading}
                className="text-sm text-[#570683] hover:underline disabled:text-gray-400 disabled:no-underline flex items-center justify-center gap-2 mx-auto"
              >
                <RefreshCw size={14} />
                {resendCooldown > 0 
                  ? `Resend code in ${resendCooldown}s` 
                  : 'Resend code'}
              </button>
            </div>

            {/* Security Notice */}
            <div className="mt-6 p-3 bg-blue-50 rounded-xl text-xs text-blue-800 border border-blue-100 text-center">
              <p>
                For your security, this code expires in 10 minutes.
              </p>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>
              Not receiving codes?{' '}
              <a href="/admin/support" className="text-[#570683] hover:underline">
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MFAVerification;
